import { IUserManagementDataLayer, LoadUsersResponse } from "@whatsllyorg/crm-components/UserManagement/types/IUserManagementDataLayer";
import { action, computed, makeObservable, observable, runInAction, toJS } from "mobx";
import type { UserManagementUser } from "@whatsllyorg/crm-components/UserManagement/types/UserManagementUser";
import { inject, injectable } from "inversify";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { postJSON, getJSON, deleteJSON, putJSON } from "@whatslly/commons/apiUtils";
import { parseOrReturnError } from "../../../services/utils";
import type {
  GetTuvisUsersResponseSuccessType,
  GetTuvisUsersResponseType,
  GetUsersResponseWhatsllyUserType,
} from "@whatslly/types/GetTuvisUsersResponse";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import type { UpdateTuvisUserStatusResponseType } from "@whatslly/types/UpdateTuvisUserStatusResponse";
import { AppIds } from "@whatslly/types/LoginProviderType";
import type { TuvisInviteUsersResponseType } from "@whatslly/types/TuvisInviteUsersResponse";
import { UpdateTuvisUserPermissionsResponse } from "@whatslly/types/UpdateTuvisUserPermissions";
import { ResendTuvisInviteResponse } from "@whatslly/types/ResendTuvisInvite";
import { hasPermission, getOptionalPermissions } from "@whatslly/commons/platformPermissions";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { DestinationsStore } from "../../Destinations/DestinationsStore";
import { modalService } from "@whatslly/ui-components/modals";
import { ViewMessagesModal } from "../../Sidecars/CommandModals/ViewMessagesModal";
import i18n from "../../../i18n/config";
import type { IOrganizationStore } from "../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../services/IOrganizationStore";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { UrlStore } from "../../../services/urlStore";
import type { UserGroup } from "../../UserGroups/UserGroupsStore";
import { UserGroupsStore } from "../../UserGroups/UserGroupsStore";
import { GetUserResponse } from "@whatslly/types/GetUser";
import { DisableUserModal } from "@whatsllyorg/crm-components/UserManagement/modals/DisableUserModal";
import { UpdateTuvisUserRequest, UpdateTuvisUserResponse } from "@whatslly/types/UpdateTuvisUser";
import type { ISidecarsStore } from "../../Sidecars/ISidecarsStore";
import { InjectSidecarsStore } from "../../Sidecars/ISidecarsStore";
import { TwilioSettingsStore } from "../../TuvisMessaging/settings/Twilio/TwilioSettingsStore";
import { WhatsAppBusinessApiSettingsStore } from "../../TuvisMessaging/settings/WhatsAppBusinessApiSettingsStore";
import { DestinationList } from "../../Destinations/utils/DestinationsList";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import { stripNumberStringToOnlyNumbers } from "@whatslly/commons/utils";
import { ConfirmModal } from "@whatslly/ui-components/modals/ConfirmModal";
import { ImportUsersModal } from "../../../modals/ImportUsersModal/ImportUsersModal";
import type { TuvisInviteNewOrgBodyType, TuvisInviteUsersBodyType } from "@whatslly/types/TuvisInviteUsersBody";
import ms from "ms";
import { unflatten } from "flat";
import type { UserSupportedLanguageType } from "@whatslly/types/User";
import { UserDefaultChannels } from "@whatslly/types/User";
import { MixpanelService } from "@whatslly/commons/mixpanel/MixpanelService";
import { OrgConfigsStore } from "../../../services/OrgConfigsStore";
import { SidecarSettingsModal } from "../../Sidecars/SidecarSettingsModal/SidecarSettingsModal";
import type { UpdateSidecar } from "@whatslly/types/sidecars-server/Sidecar";
import { SidecarChannelType } from "@whatslly/types/sidecars-server/Sidecar";
import type { UserVersionAnalytics } from "@whatslly/types/analytics/AnalyticsUserVersionQuery";

const parseTuvisMobilePhone = (raw: unknown): string | undefined => {
  if (raw === undefined || raw === null || raw === "") return undefined;
  const str = String(raw).trim();
  if (str.toLowerCase() === "true") return "true";
  const digits = stripNumberStringToOnlyNumbers(str);
  if (digits.length < 7) return undefined;
  return str;
};

const convertApiUserToUserManagementObject = (user: GetUsersResponseWhatsllyUserType): UserManagementUser => {
  return {
    _id: user._id,
    uid: user.uid,
    crmUserId: user.userId,
    name: user.fullName,
    username: user.username,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    language: user.language as UserSupportedLanguageType,
    phone: user.phone === "undefined" ? "" : user.phone,
    phones: user.phones ?? [],
    createdAt: user.createdAt,
    lastLogin: user.lastLoginDate,
    tuvisUserStatus: user.active,
    isValidCrmUser: Boolean(user.loginProvider === AppIds.SALESFORCE || user.loginProvider === AppIds.MICROSOFT_DYNAMICS),
    platform: user.platform,
    profileName: user.profileName,
    roleName: user.roleName,
    permissions: user.permissions,
    layoutFields: user.layoutFields,
    groupIds: user.groupIds,
    managerId: user.managerId,
    channels: user.channels,
    loginProvider: user.loginProvider,
    configs: user.configs,
    customFields: user.customFields ?? {},
    lastPlatformLogin: user.lastPlatformLogin,
    lastDesktopLogin: user.lastDesktopLogin,
    lastExtensionLogin: user.lastExtensionLogin,
    lastMobileLogin: user.lastMobileLogin,
    isOnline: user.isOnline,
    onlineSource: user.onlineSource,
    desktopVersion: user.desktopVersion,
  };
};

@injectable()
export class UserManagementAppDataLayer implements IUserManagementDataLayer {
  enrichUsersWithDesktopVersionsPromise: IPromiseBasedObservable<void> | undefined;

  constructor(
    @inject(InjectUserStore) private userStore: IUserStore,
    @inject(FrontLogger) private readonly logger: FrontLogger,
    @inject(DestinationsStore) private readonly destinationsStore: DestinationsStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(OrgConfigsStore) private readonly orgConfigsStore: OrgConfigsStore,
    @inject(UrlStore) private readonly urlStore: UrlStore,
    @inject(UserGroupsStore) private readonly userGroupsStore: UserGroupsStore,
    @inject(InjectSidecarsStore) private readonly sidecarsStore: ISidecarsStore,
    @inject(WhatsAppBusinessApiSettingsStore)
    private readonly wabaSettingsStore: WhatsAppBusinessApiSettingsStore,
    @inject(TwilioSettingsStore) private readonly twilioSettingsStore: TwilioSettingsStore,
    @inject(MixpanelService) private readonly mixpanelService: MixpanelService
  ) {
    this.enrichUsersWithDesktopVersionsPromise = undefined;
    makeObservable(this, {
      _searchTerm: computed,
      enrichUsersWithDesktopVersionsPromise: observable,
      phonesAndDestinations: computed,
      setSearchTerm: action,
      editModalLoadUsersList: action.bound,
      viewMessagesAction: computed,
      orgConfigs: computed,
    } as any);
  }

  get instanceConfigs() {
    return this.orgConfigsStore.orgDomainConfigs;
  }

  get optionalPermissions() {
    return getOptionalPermissions(this.userStore.user);
  }

  get orgId() {
    return this.orgStore.orgId;
  }

  get _searchTerm() {
    return this.urlStore.searchParams?.get("search") ?? "";
  }

  get restrictToDirectReports() {
    const orgFlag = this.orgConfigsStore.getConfig("restrict_to_direct_reports");
    const bypassList = this.orgConfigsStore.getConfig("restrict_to_direct_reports_bypass_list");
    return orgFlag === true && bypassList?.[this.userStore.user?._id ?? ""] !== true && !this.userStore.isInternalUser;
  }

  get phonesAndDestinations() {
    return fromPromise(
      Promise.all([
        this.destinationsStore.destinationsShallow,
        this.orgConfigs,
        this.twilioSettingsStore.loadPhones(),
        this.wabaSettingsStore.loadPhones(),
        this.sidecarsStore.updateSidecars(),
      ]).then(([destinations]) => destinations)
    );
  }

  private getUserPhones(user?: UserManagementUser) {
    const phones: string[] = [];

    if (
      user?.phones &&
      Array.isArray(user.phones) &&
      user.phones.length &&
      (user.channels?.["whatsapp-web"]?.enabled ||
        user.loginProvider === AppIds.SALESFORCE ||
        user.loginProvider === AppIds.MICROSOFT_DYNAMICS ||
        user.loginProvider === AppIds.SAML)
    ) {
      phones.push(...user.phones);
    }

    if (user?.channels?.sidecar?.length) {
      const sidecarPhones = this.sidecarsStore._sidecars
        .filter((sidecar) => user.channels?.sidecar?.includes(sidecar._id))
        .map((sidecar) => sidecar.metadata?.phone)
        .filter(Boolean) as string[];
      phones.push(...sidecarPhones);
    }

    if (user?.channels?.waba?.length) {
      const wabaPhones = this.wabaSettingsStore._phones
        .filter((phone) => user.channels?.waba?.includes(phone.id))
        .map((phone) => stripNumberStringToOnlyNumbers(phone.phone));
      phones.push(...wabaPhones);
    }

    if (user?.channels?.twilio?.length) {
      const twilioPhones = this.twilioSettingsStore._phones
        .filter((phone) => !!user.channels?.twilio?.find((phoneSidObj) => phoneSidObj.phoneSid === phone.id))
        .map((phone) => stripNumberStringToOnlyNumbers(phone.phone));
      phones.push(...twilioPhones);
    }

    return Array.from(new Set(phones));
  }

  getLimitedDateRangeOfMessages() {
    if (this.orgConfigs.state === "fulfilled" && this.orgConfigs.value) {
      return this.orgConfigs.value.configs?.limit_date_range_of_messages;
    }
    return 0;
  }

  get viewMessagesAction() {
    return this.phonesAndDestinations.case({
      pending: () => [],
      rejected: () => [],
      fulfilled: (destinations) => {
        const dests = destinations.filter((d) => d.readable && d.scope === "org");
        return dests.map((destination) => ({
          testId: `view-messages-${destination.type}`,
          children:
            dests.length > 1
              ? `${i18n.t("users_management.user_messages")} (${DestinationList.find((d) => d.type === destination.type)?.name ?? destination.type})`
              : i18n.t("users_management.user_messages"),
          icon: "chat-now",
          onClick: (user?: UserManagementUser) =>
            modalService.openModal(ViewMessagesModal, {
              enableDatesFilter: destination.enableDatesFilter,
              item: { phone: this.getUserPhones(user), externalUserId: user?.crmUserId },
              destinationId: destination._id ?? "",
              limitDateRange: this.getLimitedDateRangeOfMessages(),
            }),
          active: (user: UserManagementUser) =>
            (this.orgStore.isSameOrganization || (this.orgStore.isAccountOrg && !this.userStore.user?.isPartner)) &&
            hasPermission(this.userStore.user, UserPermissions["user_management.view_messages"]) &&
            this.getUserPhones(user).length > 0 &&
            (!this.userStore.user?.view_only_own_messages || user._id === this.userStore.user._id),
        }));
      },
    });
  }

  get orgConfigs() {
    return fromPromise(
      this.orgConfigsStore
        .getObservableConfigs([
          "allow_desktop_lite",
          "allow_imessage",
          "allow_telegram",
          "allow_mobile_call_recording",
          "show_default_auto_sync_config",
          "limit_date_range_of_messages",
          "custom_user_fields",
        ])
        .then(
          (configs) =>
            ({
              success: true,
              configs: configs ?? {},
            }) as const
        )
    );
  }

  async getUser(userId: string) {
    const orgId = (await this.orgId) ?? this.userStore.user?.organizationId;
    return await getJSON({
      urlSuffix: `/integrations/users/${userId}?orgId=${orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: import.meta.env.VITE_API_DEFAULT_POD,
    })
      .then((r) => parseOrReturnError<GetUserResponse>(r, this.logger))
      .then((r) => (r.success ? convertApiUserToUserManagementObject(r.user) : undefined));
  }

  async loadUsers(
    filters: {
      hideInactiveUsers: boolean;
      filterGroups?: string[];
      sortBy?: string;
      sort?: "asc" | "desc";
      restrictToDirectReports?: boolean;
    },
    newNextLoadMoreRequestUrl?: string,
    orgId?: string
  ): Promise<LoadUsersResponse> {
    const res = await postJSON({
      urlSuffix: "/integrations/users",
      etoken: this.userStore.etoken ?? "",
      pod: import.meta.env.VITE_API_DEFAULT_POD,
      body: JSON.stringify({
        orgId: orgId ?? this.userStore.user?.organizationId,
        limit: 200,
        nextRecordsUrl: newNextLoadMoreRequestUrl,
        onlyActive: filters.hideInactiveUsers,
        filterGroups: filters.filterGroups,
        sort: filters.sort,
        sortBy: filters.sortBy,
        name: this._searchTerm.trim(),
        restrictToDirectReports: filters.restrictToDirectReports,
      }),
    }).then((r) => parseOrReturnError<GetTuvisUsersResponseType>(r, this.logger));

    if (!res.success) {
      throw new Error();
    }

    const users = res.users.map((user) => convertApiUserToUserManagementObject(user));
    const resolvedOrgId = orgId ?? this.userStore.user?.organizationId;
    this.enrichUsersWithDesktopVersionsPromise = fromPromise(this.enrichUsersWithDesktopVersions(users, resolvedOrgId));

    return {
      users,
      licenseData: {
        numOfSeats: res.totalSeats,
        numOfUsedSeats: res.usedSeats,
        activeSeats: res.activeSeats,
      },
      allowInvite: Boolean(res.allowInvite && hasPermission(this.userStore.user, UserPermissions["user_management.invite"])),
      hideRoles: true,
      hideCrmActiveCol: true,
      hideProfile: true,
      hideTuvisProfile: !this.orgStore.isCrmOrg,
      hidePlatform: !this.orgStore.isCrmOrg,
      disableActivate: !hasPermission(this.userStore.user, UserPermissions["user_management.activate"]),
      hasMoreResults: !!res.nextRecordsUrl,
      newNextLoadMoreRequestUrl: res.nextRecordsUrl,
    };
  }

  private getUserChannelPhone(user: GetUsersResponseWhatsllyUserType) {
    const currentUser = convertApiUserToUserManagementObject(user);

    const sidecarPhonesById = user.channels?.sidecar?.reduce<Record<string, string>>((acc, sidecarId) => {
      const sidecar = this.sidecarsStore._sidecars.find((sidecar) => sidecar._id === sidecarId);

      if (sidecar && sidecar.metadata?.phone) {
        acc[sidecarId] = sidecar.metadata?.phone;
      }

      return acc;
    }, {});
    const wabaPhonesById = user.channels?.waba?.reduce<Record<string, string>>((acc, wabaId) => {
      const waba = this.wabaSettingsStore._phones.find((waba) => waba.id === wabaId);

      if (waba) {
        acc[wabaId] = waba.phone;
      }

      return acc;
    }, {});
    const twilioPhonesById = user.channels?.twilio?.reduce<Record<string, string>>((acc, twilioChannel) => {
      const twilio = this.twilioSettingsStore._phones.find((twilio) => twilio.id === twilioChannel.phoneSid);

      if (twilio) {
        acc[twilioChannel.phoneSid] = twilio.phone;
      }

      return acc;
    }, {});
    if (wabaPhonesById) currentUser.formattedChannelPhones = { waba: wabaPhonesById };
    if (sidecarPhonesById)
      currentUser.formattedChannelPhones = {
        ...currentUser.formattedChannelPhones,
        sidecar: sidecarPhonesById,
      };
    if (twilioPhonesById)
      currentUser.formattedChannelPhones = {
        ...currentUser.formattedChannelPhones,
        twilio: twilioPhonesById,
      };
    return currentUser;
  }

  private async enrichUsersWithDesktopVersions(users: UserManagementUser[], orgId?: string): Promise<void> {
    if (users.length === 0 || !orgId) return;
    try {
      const versions = await getJSON({
        urlSuffix: `/analytics/query/user-versions?orgId=${orgId}`,
        etoken: this.userStore.etoken ?? "",
        pod: import.meta.env.VITE_API_DEFAULT_ANALYTICS_POD,
      }).then((r) => parseOrReturnError<UserVersionAnalytics>(r, this.logger));
      const versionByCrmUserId = Array.isArray(versions) ? Object.fromEntries(versions.map((v) => [v.user_id, v.version])) : {};
      users.forEach((u) => {
        u.desktopVersion = u.desktopVersion || versionByCrmUserId[u.crmUserId] || "";
      });
    } catch (err) {
      this.logger.warn({ err }, "Enriching users with desktop versions failed; showing users without desktop version");
    }
  }

  async loadAllUsers(
    orgId?: string,
    filters?: {
      sortBy?: string;
      sort?: "asc" | "desc";
      hideInactiveUsers: boolean;
      filterGroups: string[];
    }
  ) {
    let users: UserManagementUser[] = [];
    let available_channels: GetTuvisUsersResponseSuccessType["available_channels"] | undefined;

    try {
      let newNextLoadMoreRequestUrl = undefined;
      do {
        const res: GetTuvisUsersResponseType = await postJSON({
          urlSuffix: "/integrations/users",
          etoken: this.userStore.etoken ?? "",
          pod: import.meta.env.VITE_API_DEFAULT_POD,
          body: JSON.stringify({
            orgId: orgId ?? this.userStore.user?.organizationId,
            limit: 2000,
            nextRecordsUrl: newNextLoadMoreRequestUrl,
            onlyActive: filters?.hideInactiveUsers,
            filterGroups: filters?.filterGroups,
            sort: filters?.sort,
            sortBy: filters?.sortBy,
          }),
        }).then((r) => parseOrReturnError<GetTuvisUsersResponseType>(r, this.logger));
        if (res.success) {
          available_channels = res.available_channels;
          newNextLoadMoreRequestUrl = res.nextRecordsUrl;
          users = users.concat(res.users.map((user) => this.getUserChannelPhone(user)));
        }
      } while (newNextLoadMoreRequestUrl);

      await this.enrichUsersWithDesktopVersions(users, orgId ?? this.userStore.user?.organizationId);
    } catch {
      this.logger.error("failed to load all users");
    }
    return { users, available_channels };
  }

  async editModalLoadUsersList(input: string) {
    const users = await this.orgStore.usersList;
    const options = users
      .filter((user) => user.isActive && user.name?.toLowerCase().includes(input.toLowerCase()))
      .map((user) => ({ label: user.name, value: user._id }));
    return { options };
  }

  async updateUser(
    users: UserManagementUser[],
    updates: Omit<UpdateTuvisUserRequest, "users" | "user" | "orgId" | "whatsllyUserName">
  ): Promise<UpdateTuvisUserPermissionsResponse> {
    const orgId = await this.orgId;
    const update = await postJSON({
      urlSuffix: "/integrations/users/update",
      etoken: this.userStore.etoken ?? "",
      pod: import.meta.env.VITE_API_DEFAULT_POD,
      body: JSON.stringify({
        orgId: orgId ?? this.userStore.user?.organizationId,
        users: users.map((user) => ({
          uid: user._id,
          whatsllyUserName: user.name,
        })),
        ...updates,
      }),
    }).then((r) => parseOrReturnError<UpdateTuvisUserPermissionsResponse>(r, this.logger));
    if (update?.success) {
      const unflattenUpdates: any = unflatten(updates);
      users.forEach((user) => {
        const clonedUserToUpdate = toJS(user);
        for (const updateKey in unflattenUpdates) {
          const newValue = unflattenUpdates[updateKey];
          if (typeof newValue === "object" && newValue !== null) {
            Object.assign(clonedUserToUpdate[updateKey as keyof typeof clonedUserToUpdate], newValue);
          } else {
            Object.assign(clonedUserToUpdate, unflattenUpdates);
          }
        }
        const { firstName, lastName } = updates;
        if (firstName || lastName) {
          const formattedName = `${firstName ? `${firstName} ` : ""}${lastName ? lastName : ""}`;
          clonedUserToUpdate.name = formattedName || "N/A";
        }
        runInAction(() => {
          Object.assign(user, clonedUserToUpdate);
        });
      });
    }
    modalService.openModal(Toast, {
      text: update?.success
        ? i18n.t("user_management.invite_user_modal.user_update_success")
        : `${i18n.t("user_management.invite_user_modal.user_update_failure")} ${update.error}`,
      type: update?.success ? "regular" : "error",
    });

    return update;
  }

  async updateUserPermissions(user: UserManagementUser, permission_bitmap: string) {
    const orgId = await this.orgId;
    const update = await postJSON({
      urlSuffix: "/integrations/users/permissions",
      etoken: this.userStore.etoken ?? "",
      pod: import.meta.env.VITE_API_DEFAULT_POD,
      body: JSON.stringify({
        user: user._id,
        orgId: orgId ?? this.userStore.user?.organizationId,
        permission_bitmap,
        whatsllyUserName: user.name,
        oldPermissionBitmap: user.permissions,
      }),
    }).then((r) => parseOrReturnError<UpdateTuvisUserPermissionsResponse>(r, this.logger));
    if (update?.success) {
      this.mixpanelService.trackUserPermissionUpdated({
        oldPermissions: user.permissions ?? "0",
        newPermissions: permission_bitmap,
        userId: user._id,
        userName: user.name,
      });

      user.permissions = permission_bitmap;
    }
    modalService.openModal(Toast, {
      text: update?.success
        ? i18n.t("user_management.invite_user_modal.permissions.update_success")
        : i18n.t("user_management.invite_user_modal.permissions.update_failure"),
      type: update?.success ? "regular" : "error",
    });
  }

  async inviteUsers({
    email,
    username,
    firstName,
    lastName,
    language,
    inviteToPlatform,
    inviteToDesktopAndExtensions,
    inviteToRemoteSync,
    defaultUserChannels,
    defaultUserPermission,
    defaultUserRemoteSyncChannels,
  }: {
    email: string;
    username: string;
    firstName: string;
    lastName: string;
    language: string;
    inviteToPlatform: boolean;
    inviteToDesktopAndExtensions: boolean;
    inviteToRemoteSync: boolean;
    defaultUserChannels: UserDefaultChannels | undefined;
    defaultUserPermission: string | undefined;
    defaultUserRemoteSyncChannels: Record<SidecarChannelType, boolean> | undefined;
  }): Promise<void> {
    const orgId = (await this.orgId) ?? this.userStore.user?.organizationId;
    const body: TuvisInviteUsersBodyType = {
      orgId: orgId,
      invitedBy: this.userStore.user?.name ?? "N/A",
      inviteToPlatform,
      inviteToDesktopAndExtensions,
      inviteToRemoteSync,
      data: [{ email, username, firstName, lastName, language }],
      defaultUserChannels,
      defaultUserPermission,
      defaultUserRemoteSyncChannels,
    };
    const res = await postJSON({
      urlSuffix: "/integrations/users/invites",
      etoken: this.userStore.etoken ?? "",
      pod: import.meta.env.VITE_API_DEFAULT_POD,
      body: JSON.stringify(body),
    }).then((r) => parseOrReturnError<TuvisInviteUsersResponseType>(r, this.logger));
    this.mixpanelService.trackUserInvited({
      success: res.success,
      inviteToPlatform,
      inviteToApplications: inviteToDesktopAndExtensions,
    });

    if (res.success && res.uids.length === 1) {
      this.mixpanelService.trackUserInvited({
        success: true,
        inviteToPlatform,
        inviteToApplications: inviteToDesktopAndExtensions,
      });
      this.urlStore.navigate(`/admin/users/${res.uids[0]}?orgId=${res.orgId}`);
    }
    if (!res.success) {
      modalService.openModal(Toast, {
        text: i18n.t("user_management.invite_users_failure", { error: res.error }),
        type: "error",
      });
    }
  }

  async resendInvite(users: UserManagementUser[]) {
    const orgId = (await this.orgId) ?? this.userStore.user?.organizationId;
    const result = await postJSON({
      urlSuffix: `/integrations/users/invites/resend`,
      etoken: this.userStore.etoken ?? "",
      pod: import.meta.env.VITE_API_DEFAULT_POD,
      body: JSON.stringify({
        inviteToPlatform: true,
        orgId,
        uids: users.map((user) => user._id),
      }),
    }).then((r) => parseOrReturnError<ResendTuvisInviteResponse>(r, this.logger));
    modalService.openModal(Toast, {
      text: result?.success ? i18n.t("user_management.actions.resend_success") : i18n.t("user_management.actions.resend_failure"),
      type: result?.success ? "regular" : "error",
    });
  }

  async deleteUser(user: UserManagementUser) {
    const res = await modalService.openModal(ConfirmModal, {
      title: i18n.t("user_management.actions.confirm_delete_title"),
      body: i18n.t("user_management.actions.confirm_delete_text"),
    }).promise;
    if (!res?.result) {
      return;
    }
    const orgId = (await this.orgId) ?? this.userStore.user?.organizationId;
    const result = await deleteJSON({
      urlSuffix: `/integrations/users/${user._id}?orgId=${orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: import.meta.env.VITE_API_DEFAULT_POD,
      body: "{}",
    }).then((r) => parseOrReturnError<UpdateTuvisUserResponse>(r, this.logger));
    modalService.openModal(Toast, {
      text: result?.success ? i18n.t("user_management.actions.delete_success") : i18n.t("user_management.actions.delete_failure"),
      type: result?.success ? "regular" : "error",
    });
  }

  async updateUserStatus(users: UserManagementUser[], newStatus: boolean, orgId?: string): Promise<UpdateTuvisUserStatusResponseType> {
    return await postJSON({
      urlSuffix: "/integrations/users/updateStatus",
      etoken: this.userStore.etoken ?? "",
      pod: import.meta.env.VITE_API_DEFAULT_POD,
      body: JSON.stringify({
        orgId: orgId ?? this.userStore.user?.organizationId,
        newStatus,
        users: users.map((user) => ({
          whatsllyUserName: user.name,
          crmUserId: user.crmUserId,
          uid: user._id,
        })),
      }),
    }).then((r) => parseOrReturnError<UpdateTuvisUserStatusResponseType>(r, this.logger));
  }

  async changeSidecarSettings(sidecarIds: string[]) {
    const sidecars = this.sidecarsStore._sidecars.filter((sidecar) => sidecarIds.includes(sidecar._id));
    if (sidecars.length === 0) {
      return;
    }
    const autoReinvite = new Set(sidecars.map((sidecar) => sidecar.metadata?.autoReinvite ?? false));
    const notifyEmail = new Set(sidecars.map((sidecar) => sidecar.metadata?.notifyEmail ?? false));
    const periodicReinvite = new Set(sidecars.map((sidecar) => sidecar.metadata?.periodicReinvite ?? false));

    const a = (values: Set<boolean>) => {
      if (values.size === 1) {
        return values.values().next().value ? "on" : "off";
      }
      if (values.size === 0) {
        return "off";
      }
      return "partial";
    };

    const settings = await modalService.openModal(SidecarSettingsModal, {
      options: {
        hideFrequencyOptions: true,
        autoReinvite: a(autoReinvite),
        notifyEmail: a(notifyEmail),
        periodicReinvite: a(periodicReinvite),
        locale: i18n.language,
      },
    }).promise;
    if (!settings || sidecarIds.length === 0) {
      return;
    }
    const urlSearchParams = await this.orgStore.selectedOrg.then((org) => (org ? `?orgId=${org.value}` : ""));
    const body = {
      ids: sidecars.map((sidecar) => sidecar._id),
      settings: {
        autoReinvite: settings.autoReinvite === "partial" ? undefined : settings.autoReinvite === "on",
        notifyEmail: settings.notifyEmail === "partial" ? undefined : settings.notifyEmail === "on",
        periodicReinvite: settings.periodicReinvite === "partial" ? undefined : settings.periodicReinvite === "on",
      },
    };
    const response: UpdateSidecar = await putJSON({
      urlSuffix: `/sidecars/bulk/settings${urlSearchParams}`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify(body),
    }).then((res) => res.json());
    if (!response.success) {
      modalService.openModal(Toast, {
        text: i18n.t("user_management.change_sidecars_settings_failure"),
        type: `error`,
        timeout: ms("1min"),
      });
      return;
    }
    await this.sidecarsStore.updateSidecars();
    modalService.openModal(Toast, {
      text: i18n.t("user_management.change_sidecars_settings_success"),
      type: "regular",
      timeout: ms("30sec"),
    });
  }

  private async changeUserStatus(users: UserManagementUser[], newStatus: boolean) {
    runInAction(() => {
      users.forEach((user) => {
        user.tuvisUserStatus = newStatus;
      });
    });
    try {
      const update = await this.updateUserStatus(users, newStatus, await this.orgId);
      if (!update?.success) {
        runInAction(() => {
          users.forEach((user) => {
            user.tuvisUserStatus = !newStatus;
          });
        });

        modalService.openModal(Toast, {
          text: update?.success
            ? i18n.t("user_management.invite_user_modal.user_update_success")
            : `${i18n.t("user_management.invite_user_modal.user_update_failure")} ${update.error}`,
          type: update?.success ? "regular" : "error",
        });
      }
    } catch {
      runInAction(() => {
        users.forEach((user) => {
          user.tuvisUserStatus = !newStatus;
        });
      });
    }
  }

  async onUserStatusChange(user: UserManagementUser[], newStatus: boolean) {
    if (newStatus) {
      await this.changeUserStatus(user, true);
      return;
    }
    const res = await modalService.openModal(DisableUserModal).promise;
    if (res?.result) {
      await this.changeUserStatus(user, false);
    }
  }

  setSearchTerm(search: string): void {
    this.urlStore.setSearchParams({ search });
  }

  get groupNames() {
    return this.userGroupsStore.groupsList;
  }
  get casAssignGroup() {
    return this.userGroupsStore.casAssignGroup;
  }
  async addUserToGroup(group: Omit<UserGroup, "users">, user: UserManagementUser) {
    const original = user.groupIds;
    runInAction(() => (user.groupIds = [...(user.groupIds ?? []), group._id]));
    const result = await this.userGroupsStore.addUserToGroup(group, {
      _id: user._id,
      name: user.name,
      channels: user.channels,
      username: user.username,
      lastLogin: user.lastLogin,
      loginProvider: user.loginProvider,
      userId: user.crmUserId,
    });
    if (!result?.success) {
      runInAction(() => (user.groupIds = original));
    }
  }
  async removeUserFromGroup(group: Omit<UserGroup, "users">, user: UserManagementUser) {
    const result = await this.userGroupsStore.removeUserFromGroup?.(group, {
      _id: user._id,
      name: user.name,
      channels: user.channels,
      username: user.username,
      lastLogin: user.lastLogin,
      loginProvider: user.loginProvider,
      userId: user.crmUserId,
    });
    if (result?.success) {
      runInAction(() => (user.groupIds = user.groupIds?.filter((id) => id !== group._id)));
    }
  }

  async importUsersCsv() {
    const result = await modalService.openModal(ImportUsersModal, { instanceConfigs: this.orgConfigsStore.orgDomainConfigs }).promise;
    if (!result || !result.csvContent) {
      return;
    }
    const { data, header } = result.csvContent;
    const firstNameHeader = header.find((name) => name.toLowerCase() === "first name");
    const lastNameHeader = header.find((name) => name.toLowerCase() === "last name");
    const usernameHeader = header.find((name) => name.toLowerCase() === "username");
    const emailHeader = header.find((name) => name.toLowerCase() === "email");
    const groupHeader = header.find((name) => name.toLowerCase() === "group");
    const tuvisMobilePhoneHeader =
      header.find((name) => name.toLowerCase() === "remote sync phone") || header.find((name) => name.toLowerCase() === "tuvis mobile phone");
    const managerHeader = header.find((name) => name.toLowerCase() === "manager");
    const phonesHeader = header.find((name) => name.toLowerCase() === "phones");
    if (!firstNameHeader || !lastNameHeader || !emailHeader) {
      modalService.openModal(Toast, {
        text: i18n.t("user_management.import_users_csv_invalid_format", {
          header: header.join(", "),
        }),
        type: "error",
      });
      return;
    }
    // Build custom fields map from org config
    const customFieldsList = this.orgConfigs.state === "fulfilled" ? (this.orgConfigs.value?.configs?.custom_user_fields ?? []) : [];
    const formattedData: TuvisInviteNewOrgBodyType["data"] = Object.values(data).map((value) => {
      const customFields: Record<string, string> = {};
      if (Array.isArray(customFieldsList) && customFieldsList.length) {
        for (const field of customFieldsList) {
          const normalizedField = field.normalize("NFC");
          if (value[normalizedField]) customFields[field] = value[normalizedField];
        }
      }
      const phones: string[] | undefined =
        phonesHeader && value[phonesHeader]
          ? value[phonesHeader]
              .split(/[;,|]/)
              .map((p: string) => stripNumberStringToOnlyNumbers(p))
              .filter((p: string) => p.length > 0)
          : undefined;
      return {
        firstName: value[firstNameHeader],
        lastName: value[lastNameHeader],
        email: value[emailHeader],
        username: usernameHeader ? value[usernameHeader] : undefined,
        group: groupHeader ? value[groupHeader] : undefined,
        tuvisMobilePhone: tuvisMobilePhoneHeader ? parseTuvisMobilePhone(value[tuvisMobilePhoneHeader]) : undefined,
        manager: managerHeader ? value[managerHeader] : undefined,
        phones,
        customFields: Object.keys(customFields).length ? customFields : undefined,
      };
    });
    if (formattedData.length === 0) {
      modalService.openModal(Toast, {
        text: i18n.t("user_management.import_users_csv_success", {
          numOfNewUsers: 0,
          numOfExistingUsers: 0,
        }),
        type: "regular",
        timeout: ms("30sec"),
      });
      return;
    }
    const orgId = (await this.orgId) ?? this.userStore.user?.organizationId;
    const body: TuvisInviteUsersBodyType = {
      orgId: orgId,
      invitedBy: this.userStore.user?.name ?? "N/A",
      inviteToPlatform: result.inviteToPlatform,
      inviteToDesktopAndExtensions: result.inviteToDesktopAndExtensions,
      notifyInviterOnSidecarEvents: result.notifyInviterOnSidecarEvents,
      updateExisting: result.updateExisting,
      data: formattedData,
      defaultUserPermission: result.defaultUserPermission,
      defaultUserChannels: result.defaultUserChannels,
      defaultUserRemoteSyncChannels: result.defaultUserRemoteSyncChannels,
      inviteToRemoteSync: result.inviteToRemoteSync,
    };
    const res = await postJSON({
      urlSuffix: "/integrations/users/invites",
      etoken: this.userStore.etoken ?? "",
      pod: import.meta.env.VITE_API_DEFAULT_POD,
      body: JSON.stringify(body),
    }).then((r) => parseOrReturnError<TuvisInviteUsersResponseType>(r, this.logger));
    if (res.success) {
      this.mixpanelService.trackUsersImported({
        userCount: res.uids.length,
        remoteSync: formattedData.filter((user) => Boolean(user.tuvisMobilePhone)).length,
        success: true,
        ignoredCount: Math.max(0, formattedData.length - res.uids.length - (result.updateExisting ? (res.updatedCount ?? 0) : 0)),
      });
      const numOfUpdatedUsers = result.updateExisting ? (res.updatedCount ?? Math.max(0, formattedData.length - res.uids.length)) : 0;
      const text = result.updateExisting
        ? i18n.t("user_management.import_users_csv_success_update", {
            numOfNewUsers: res.uids.length,
            numOfUpdatedUsers,
          })
        : i18n.t("user_management.import_users_csv_success", {
            numOfNewUsers: res.uids.length,
            numOfExistingUsers: formattedData.length - res.uids.length,
          });
      modalService.openModal(Toast, { text, type: "regular", timeout: ms("30sec") });
    } else {
      modalService.openModal(Toast, {
        text: i18n.t("user_management.import_users_csv_failure", { error: res.error }),
        type: "error",
      });
      this.mixpanelService.trackUsersImported({
        userCount: formattedData.length,
        remoteSync: 0,
        success: false,
        ignoredCount: 0,
      });
    }
    return res.success;
  }
}
